import {
  BlocksViewNode,
  Controller,
  InteractionMode,
  Interactions,
} from '../types';
import { isGroup } from '../viewModel$/util';
import { HitTestResult, hitTestNodePosition } from '../visual/node/node';
import { renderGraph } from '../visual/visual';

import { rectCenter, rectHeight, rectWidth } from '../misc/geometry';
import { clamp } from 'lodash';
import { IconName } from '@ardoq/icons';
import { SettingsConfig, SettingsType } from '@ardoq/view-settings';

import {
  linkContextMenuOptions,
  nodeContextMenuOptions,
  rootContextMenuOptions,
} from './contextMenuOptions';
import { type ContextMenuAppState } from 'contextMenus/contextMenuRequested';
import type { DropdownItem } from '@ardoq/dropdown-menu';
import { isMac } from '@ardoq/common-helpers';
import { openSingleUrlFieldValue } from 'tabview/graphViews/openSingleUrlFieldValue';
import { updateHover } from './interactions';
import { t, type I18nLocale } from '@ardoq/i18n';
export const createPresentationInteractions = (
  controller: Controller
): Interactions => {
  enum InteractionState {
    idle = 0,
    armed = 1,
    pan = 2,
    createReference = 3,
  }

  let interactionState: InteractionState = InteractionState.idle;

  const canvas = controller.canvas;
  const projection = controller.projection;
  const selection = controller.selection;
  const commands = controller.commands;
  const ctx = canvas.getContext('2d', { alpha: false })!;

  const attach = () => {
    const canvas = controller.canvas;

    // clear the current selection
    // populate it from the graph

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerLeave);
    canvas.addEventListener('lostpointercapture', onLostPointerCapture);
    canvas.addEventListener('wheel', onWheel);
    canvas.addEventListener('keydown', onKeyDown);
    canvas.addEventListener('dblclick', onDoubleClick);
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('focusin', onFocus);
    canvas.addEventListener('focusout', onFocus);
  };

  const detach = () => {
    const canvas = controller.canvas;

    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    canvas.removeEventListener('lostpointercapture', onLostPointerCapture);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('keydown', onKeyDown);
    canvas.removeEventListener('dblclick', onDoubleClick);
    canvas.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('focusin', onFocus);
    canvas.removeEventListener('focusout', onFocus);
  };

  const renderCanvas = () => {
    /* other than the old edit interactions this completely generic */

    ctx.setTransform(projection.matrix());
    const done = renderGraph(
      controller.graph,
      projection.window(true),
      controller.getHighlight().size() !== 0 ? 'lowlight' : 'normal',
      ctx
    );

    return done;
  };

  const cursor = () => {
    if (
      interactionState === InteractionState.idle &&
      (controller.hover.node() || controller.hover.link())
    ) {
      return 'pointer'; // pointy finger
    }

    if (interactionState === InteractionState.armed) {
      if (controller.hover.node() || controller.hover.link()) {
        return 'pointer'; // pointy finger
      }

      return 'grabbing'; // grabby hand (fairly sure that this won't happen)
    }

    if (interactionState === InteractionState.pan) {
      return 'grabbing';
    }

    return 'grab';
  };

  const onPointerDown = (e: PointerEvent) => {
    if (interactionState === InteractionState.idle && e.button === 0) {
      controller.pointerDevice = [e.offsetX, e.offsetY];
      controller.pointerWorld = projection.toWorld(controller.pointerDevice);

      if (interactionState === InteractionState.idle) {
        canvas.setPointerCapture(e.pointerId); // grab the pointer
        controller.pointerId = canvas.hasPointerCapture(e.pointerId)
          ? e.pointerId
          : NaN;
      }

      if (isFinite(controller.pointerId)) {
        interactionState = InteractionState.armed;

        controller.pointerDownDevice = controller.pointerDevice;
        controller.pointerDownWorld = controller.pointerWorld;
      }
    }

    controller.cancelTooltip();
    canvas.style.cursor = cursor();
  };

  const onPointerMove = (e: PointerEvent) => {
    controller.pointerDevice = [e.offsetX, e.offsetY];
    controller.pointerWorld = projection.toWorld(controller.pointerDevice);

    if (
      interactionState === InteractionState.armed &&
      e.pointerId === controller.pointerId
    ) {
      const distance = Math.hypot(
        controller.pointerDevice[0] - controller.pointerDownDevice[0],
        controller.pointerDevice[1] - controller.pointerDownDevice[1]
      );

      // pointer down then move more than five pels turns this into a drag, which means "pan" for a viewer
      interactionState = distance > 5 ? InteractionState.pan : interactionState;
    }

    if (
      interactionState === InteractionState.pan &&
      e.pointerId === controller.pointerId
    ) {
      projection.adjustToPoint(
        controller.pointerDownWorld,
        controller.pointerDevice,
        false
      );
      controller.pointerWorld = projection.toWorld(controller.pointerDevice);
      controller.renderCanvas();
    }

    if (interactionState === InteractionState.idle) {
      const oldItem = controller.hover.link() ?? controller.hover.node();

      updateHover(controller, null);
      const newPointerNode = controller.hover.node();
      const newPointerLink = controller.hover.link();
      const newItem = newPointerLink ?? newPointerNode;

      if (newItem !== oldItem) {
        /* "highlight linked" effect */
        const pointerNode = controller.hover.node();
        const isOpenGroup =
          pointerNode && isGroup(pointerNode) && pointerNode.open;
        /** this node and its connections will be highlighted. the rest of the graph will be dimmed. open groups do not receive this effect. when the value of linkedNode is null, this will clear the highlights. */
        const linkedNode =
          pointerNode && !isOpenGroup ? controller.hover.node() : null;

        controller.highlightLinked(linkedNode);

        /* mouse move has caused the hover item to change; heaven knows what that does visually */
        controller.renderCanvas();
      }
    }

    canvas.style.cursor = cursor();
  };

  const onPointerUp = (e: PointerEvent) => {
    if (
      e.button !== 0 ||
      !Number.isFinite(controller.pointerId) ||
      e.pointerId !== controller.pointerId
    ) {
      return;
    }

    const hoverNode = controller.hover.node();

    const pointerDownHit =
      hoverNode && hitTestNodePosition(hoverNode, controller.pointerDownWorld);

    if (
      interactionState === InteractionState.armed &&
      pointerDownHit === HitTestResult.LINK_ICON
    ) {
      const urlFieldValues = hoverNode?.urlFieldValues;
      if (urlFieldValues?.length) {
        openSingleUrlFieldValue(urlFieldValues, canvas);
      }
      interactionState = InteractionState.idle;
      canvas.releasePointerCapture(controller.pointerId);
      canvas.style.cursor = cursor();
      return;
    }

    if (
      interactionState === InteractionState.armed &&
      pointerDownHit !== HitTestResult.EXPANDER
    ) {
      /* handle selection */
      const isExtendedSelectModifier = isMac() ? e.metaKey : e.shiftKey;
      const { hover, selection } = controller;

      if (isExtendedSelectModifier) {
        if (hover.isSubsetOf(selection)) {
          for (const item of hover.values()) {
            selection.delete(item);
          }
        } else {
          for (const item of hover.values()) {
            selection.add(item);
          }
        }
      } else {
        selection.clear();

        for (const item of hover.values()) {
          selection.add(item);
        }
      }

      if (hoverNode?.isComponent && hoverNode.modelId) {
        commands.onComponentClick(hoverNode.modelId);
      }

      interactionState = InteractionState.idle;
      controller.renderCanvas();
    }

    if (
      interactionState === InteractionState.armed &&
      hoverNode &&
      pointerDownHit === HitTestResult.EXPANDER
    ) {
      controller.setOpen(hoverNode, !hoverNode.open);
    }

    interactionState = InteractionState.idle;
    canvas.releasePointerCapture(controller.pointerId); // release the capture
    canvas.style.cursor = cursor();
  };

  // this is completely generic and could be moved out
  const onPointerLeave = (e: PointerEvent) => {
    if (!e.isPrimary) {
      /* ignore pointers that I don't care about */

      return;
    }

    controller.pointerDevice = [NaN, NaN];
    controller.pointerWorld = [NaN, NaN];
    controller.scheduleTooltipCancel();
  };

  // this is almost completely generic and could be moved out
  const onLostPointerCapture = (_e: PointerEvent) => {
    switch (interactionState) {
      case InteractionState.idle:
        break;

      case InteractionState.armed:
        // disarm
        break;

      case InteractionState.pan:
        // put view back to original position
        break;
    }

    interactionState = InteractionState.idle;
    canvas.style.cursor = cursor();
  };

  // this is completely generic and could be moved out
  const onKeyDown = (e: KeyboardEvent) => {
    const selection = controller.selection;
    const pointerNode = controller.hover.node();

    const rc = projection.window(false);
    const speed = e.shiftKey ? 3 : 1;
    let handled = true;

    switch (e.key) {
      case 'PageUp':
        controller.zoomTo(rectCenter(rc), 1.1 * speed, true);
        break;

      case 'PageDown':
        controller.zoomTo(rectCenter(rc), 0.9 / speed, true);
        break;

      case 'ArrowLeft':
        controller.panBy([speed * 0.05 * rectWidth(rc), 0]);
        break;

      case 'ArrowUp':
        controller.panBy([0, speed * 0.05 * rectHeight(rc)]);
        break;

      case 'ArrowRight':
        controller.panBy([-speed * 0.05 * rectWidth(rc), 0]);
        break;

      case 'ArrowDown':
        controller.panBy([0, -speed * 0.05 * rectHeight(rc)]);
        break;

      case 'Enter': // possibly an action of some kind
        if (e.shiftKey && selection.size() !== 0) {
          controller.fit(selection.bounds(), true);
        } else {
          controller.fit(controller.graph.root.bounds, true);
        }
        break;

      case ' ': // possibly an action of some kind
        if (pointerNode && isGroup(pointerNode)) {
          controller.setOpen(pointerNode, !pointerNode.open);
        }
        break;

      case 'Escape':
        controller.cancelTooltip();
        if (canvas.hasPointerCapture(controller.pointerId)) {
          canvas.releasePointerCapture(controller.pointerId);
        }
        break;

      case 'Tab':
        handled = false;
        break;

      default:
        handled = false;
        break;
    }

    if (handled) {
      controller.pointerWorld = projection.toWorld(controller.pointerDevice); // recalculate the world position just in case the projection got changed
      controller.cancelTooltip();
      controller.renderCanvas(); // some of the events will already have done this, but rendering twice is a free operation
      e.stopPropagation();
    }
  };

  // this is completely generic and could be moved out
  const onWheel = (e: WheelEvent) => {
    const speed = e.ctrlKey ? 4 : 1;
    const deltaY = (speed * clamp(e.deltaY, -800, 800)) / 1000;

    e.stopPropagation();
    e.stopImmediatePropagation();
    e.preventDefault();

    controller.pointerDevice = [e.offsetX, e.offsetY];
    controller.pointerWorld = projection.toWorld(controller.pointerDevice);
    controller.cancelTooltip();
    controller.zoomTo(controller.pointerWorld, 1 - deltaY, false);
  };

  // this is almost completely generic and could be moved out
  const onDoubleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const pointerNode = controller.hover.node();

    if (pointerNode?.modelId) {
      commands.onComponentDoubleClick(pointerNode.modelId, [
        pointerNode.modelId,
      ]);
    }
  };

  /* handle selection */

  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    commands.openContextMenu({
      position: { left: e.clientX, top: e.clientY },
      getItems: contextMenuOptions,
      onOpened: () => controller.cancelTooltip(),
    });

    /* tinker with the selection */

    const { selection, hover } = controller;

    if (!hover.isSubsetOf(selection)) {
      selection.clear();
      for (const item of hover.values()) {
        selection.add(item);
      }
    }

    controller.renderCanvas();
  };

  const onFocus = (_e: FocusEvent) => {
    // view has gained or lost focus
    // this should be visible on the screen in some way
    controller.renderCanvas();
  };

  const contextMenuOptions = (
    appState: ContextMenuAppState
  ): DropdownItem[] => {
    if (interactionState !== InteractionState.idle) {
      return [];
    }

    const pointerLink = controller.hover.link();
    const pointerNode = controller.hover.node();

    return pointerLink
      ? linkContextMenuOptions(controller, pointerLink, appState)
      : pointerNode
        ? nodeContextMenuOptions(
            controller!,
            pointerNode,
            new Set<BlocksViewNode>(selection.nodes()),
            appState
          )
        : rootContextMenuOptions(controller!);
  };

  const viewActionsConfigs = (i18nLocale: I18nLocale): SettingsConfig[] => {
    const configs: SettingsConfig[] = [];
    const hasGroups = true; // are there any groups in the set of nodes
    const selection = controller.selection;

    if (!controller.isPresentationMode) {
      configs.push({
        id: 'id_editmode',
        type: SettingsType.TOGGLE,
        label: t(i18nLocale, 'appLayout:viewPaneHeader.editMode', 'Edit mode'),
        onClick: () => controller.setInteractionMode(InteractionMode.Viewpoint),
        iconName: IconName.EDIT,
        isDisabled: false,
      });
    }

    configs.push({
      id: 'id_collapse_all',
      type: SettingsType.BUTTON,
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.collapseAll',
        'Collapse all'
      ),
      onClick: controller.collapseAll,
      iconName: IconName.COLLAPSE_ALL,
      isDisabled: !hasGroups,
    });
    configs.push({
      id: 'id_expand_all',
      type: SettingsType.BUTTON,
      label: t(i18nLocale, 'appLayout:viewPaneHeader.expandAll', 'Expand all'),
      onClick: controller.expandAll,
      iconName: IconName.EXPAND_ALL,
      isDisabled: !hasGroups,
    });

    configs.push({ id: 'id_divider3', type: SettingsType.DIVIDER });

    configs.push({
      id: 'id_zoom_in',
      type: SettingsType.BUTTON,
      label: t(i18nLocale, 'appLayout:viewPaneHeader.zoomIn', 'Zoom in'),
      onClick: controller.zoomIn,
      iconName: IconName.ZOOM_IN,
      isDisabled: false,
    });
    configs.push({
      id: 'id_zoom_out',
      type: SettingsType.BUTTON,
      label: t(i18nLocale, 'appLayout:viewPaneHeader.zoomOut', 'Zoom out'),
      onClick: controller.zoomOut,
      iconName: IconName.ZOOM_OUT,
    });
    configs.push({
      id: 'id_zoom_to_fit',
      type: SettingsType.BUTTON,
      label: t(i18nLocale, 'appLayout:viewPaneHeader.fit', 'Fit'),
      onClick: () => {
        controller.fit(
          selection.size() === 0
            ? controller.graph.root.bounds
            : selection.bounds(),
          true
        );
      },
      iconName: IconName.ZOOM_TO_FIT,
    });

    return configs;
  };

  return {
    attach,
    detach: detach,
    renderCanvas,
    viewActionsConfigs: viewActionsConfigs,
  };
};
